/**
 * Main Interactive Application Controller for Kazakan Cognitive Test
 * Implements states: Intro -> Active Test -> Results -> Solution Review
 * Zero dependencies, pure accessible DOM, offline capable.
 */

import { createSessionSeed, createPRNG } from './prng.js';
import { DOMAINS, createPracticeQuestion } from './question-bank.js';
import { AdaptiveTestSession } from './adaptive-engine.js';
import { computeSessionScores } from './scoring.js';
import { showConfirm } from '../../core/modal.js';
import { HUMAN_IQ_TIERS, SMARTEST_HUMANS, getUserIqTier, calculateEstimatedIq } from './benchmarks.js';
import { trackEvent } from '../../core/analytics.js';

export function initIqTest(containerId = 'iq-test-app') {
  const container = document.getElementById(containerId);
  if (!container) return;

  const lang = document.documentElement.lang === 'en' ? 'en' : 'ar';
  const isAr = lang === 'ar';

  const getNow = () => {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    if (typeof window !== 'undefined' && window.performance && typeof window.performance.now === 'function') {
      return window.performance.now();
    }
    return Date.now();
  };

  let prng = createPRNG(createSessionSeed());
  let session = null;
  let currentQuestion = null;
  let questionStartTime = 0;
  let timerInterval = null;
  let remainingSeconds = 20 * 60; // 20 minutes total (1200s)
  let lastTimeMs = getNow();
  let latestScores = null;
  let pendingSelectedIndex = null;

  // Render Initial View (Intro & Practice)
  renderIntroView();

  function renderIntroView() {
    clearInterval(timerInterval);
    const practiceQ = createPracticeQuestion();

    if (!container.querySelector('#iq-start-btn')) {
      container.innerHTML = `
      <div class="iq-flow-card iq-intro-card" role="region" aria-label="${isAr ? 'مقدمة الاختبار' : 'Test Introduction'}">
        <header class="iq-intro-header">
          <div class="iq-intro-badge">
            <span class="iq-badge-pulse-dot" aria-hidden="true"></span>
            <span>${isAr ? 'تقييم القدرات المعرفية المتكيف • CAT' : 'Adaptive Cognitive Assessment • CAT'}</span>
          </div>
          <h2 class="iq-intro-title">${isAr ? 'مقياس القدرات العقلية والتفكير المنطقي' : 'Cognitive Ability & Logical Reasoning Benchmark'}</h2>
          <p class="iq-intro-desc">${isAr ? 'تقييم تحليلي مقنن يقيس 7 أبعاد إدراكية عبر 28 مهمة متكيفة تلقائياً، في بيئة هادئة وخاصة 100%.' : 'A standardized benchmark measuring 7 cognitive dimensions across 28 adaptive items, in a 100% private client-side environment.'}</p>
        </header>

        <!-- Clean Unified Specs Bar -->
        <div class="iq-specs-strip" role="group" aria-label="${isAr ? 'مواصفات الاختبار' : 'Assessment Specifications'}">
          <div class="iq-spec-item">
            <span class="iq-spec-val iq-spec-highlight">28</span>
            <span class="iq-spec-lbl">${isAr ? 'مهمة متكيفة (CAT)' : 'Adaptive Items'}</span>
          </div>
          <div class="iq-spec-divider" aria-hidden="true"></div>
          <div class="iq-spec-item">
            <span class="iq-spec-val">20:00</span>
            <span class="iq-spec-lbl">${isAr ? 'الحد الزمني الأقصى' : 'Max Time Limit'}</span>
          </div>
          <div class="iq-spec-divider" aria-hidden="true"></div>
          <div class="iq-spec-item">
            <span class="iq-spec-val">7</span>
            <span class="iq-spec-lbl">${isAr ? 'محاور معرفية مقاسة' : 'Cognitive Domains'}</span>
          </div>
          <div class="iq-spec-divider" aria-hidden="true"></div>
          <div class="iq-spec-item">
            <span class="iq-spec-val">100%</span>
            <span class="iq-spec-lbl">${isAr ? 'محلي وخاص بالكامل' : 'Client-Side Private'}</span>
          </div>
        </div>

        <!-- Centered Primary Start Action in the Middle -->
        <div class="iq-intro-cta-wrapper">
          <button id="iq-start-btn" class="btn-primary iq-start-btn">
            <span>${isAr ? 'ابدأ تقييم القدرات الآن' : 'Start Cognitive Benchmark Now'}</span>
            <svg class="iq-start-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="${isAr ? 'M19 12H5M12 19l-7-7 7-7' : 'M5 12h14M12 5l7 7-7 7'}"/>
            </svg>
          </button>
          <div class="iq-privacy-assure">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span>${isAr ? 'معالجة محلية 100% على جهازك دون تسجيل أو إرسال أي بيانات.' : '100% Client-side local processing: zero registration, zero tracking.'}</span>
          </div>
        </div>

        <!-- Optional Collapsed Sample Item Accordion -->
        <details class="iq-sample-accordion">
          <summary class="iq-sample-summary">
            <div class="iq-sample-summary-left">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>${isAr ? 'كيف تبدو الأسئلة؟ (مسألة توضيحية للمعاينة)' : 'How do questions look? (Preview sample item)'}</span>
            </div>
            <span class="iq-sample-preview-tag">${isAr ? 'معاينة سريعة' : 'Preview'}</span>
          </summary>
          <div class="iq-sample-content">
            <h3 class="iq-question-text iq-sample-q-text" dir="auto">${isAr ? practiceQ.questionText_ar : practiceQ.questionText_en}</h3>
            <div class="iq-stimulus-container iq-sample-stimulus">${isAr ? (practiceQ.stimulusHtml_ar || practiceQ.stimulusHtml) : (practiceQ.stimulusHtml_en || practiceQ.stimulusHtml)}</div>
            <section class="iq-options-container iq-sample-options-container" role="region" aria-label="${isAr ? 'خيارات الإجابة التوضيحية' : 'Sample answer choices'}">
              <div class="iq-options-header">
                <span class="iq-options-title">${isAr ? 'خيارات الإجابة:' : 'Sample Answer Choices:'}</span>
                <span class="iq-options-hint">${isAr ? '(اضغط على الخيار للتجربة وفحص الإجابة)' : '(Click an option to test and verify)'}</span>
              </div>
              <div class="iq-options-grid iq-sample-options has-svg-options" id="practice-options-grid">
                ${practiceQ.options.map((opt, idx) => `
                  <button class="iq-option-btn practice-opt-btn" data-idx="${idx}" aria-label="${opt.id}: ${isAr ? opt.text_ar : opt.text_en}">
                    <span class="iq-opt-letter">${opt.id}</span>
                    <span class="iq-opt-content" dir="auto">${opt.svg ? opt.svg : (isAr ? opt.text_ar : opt.text_en)}</span>
                  </button>
                `).join('')}
              </div>
            </section>
            <div id="practice-feedback" class="iq-practice-feedback" style="display:none;" aria-live="polite"></div>
          </div>
        </details>
      </div>
    `;
    } else if (!container.querySelector('.iq-sample-accordion')) {
      const introCard = container.querySelector('.iq-intro-card');
      if (introCard) {
        const sampleDetails = document.createElement('details');
        sampleDetails.className = 'iq-sample-accordion';
        sampleDetails.innerHTML = `
          <summary class="iq-sample-summary">
            <div class="iq-sample-summary-left">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>${isAr ? 'كيف تبدو الأسئلة؟ (مسألة توضيحية للمعاينة)' : 'How do questions look? (Preview sample item)'}</span>
            </div>
            <span class="iq-sample-preview-tag">${isAr ? 'معاينة سريعة' : 'Preview'}</span>
          </summary>
          <div class="iq-sample-content">
            <h3 class="iq-question-text iq-sample-q-text" dir="auto">${isAr ? practiceQ.questionText_ar : practiceQ.questionText_en}</h3>
            <div class="iq-stimulus-container iq-sample-stimulus">${isAr ? (practiceQ.stimulusHtml_ar || practiceQ.stimulusHtml) : (practiceQ.stimulusHtml_en || practiceQ.stimulusHtml)}</div>
            <section class="iq-options-container iq-sample-options-container" role="region" aria-label="${isAr ? 'خيارات الإجابة التوضيحية' : 'Sample answer choices'}">
              <div class="iq-options-header">
                <span class="iq-options-title">${isAr ? 'خيارات الإجابة:' : 'Sample Answer Choices:'}</span>
                <span class="iq-options-hint">${isAr ? '(اضغط على الخيار للتجربة وفحص الإجابة)' : '(Click an option to test and verify)'}</span>
              </div>
              <div class="iq-options-grid iq-sample-options has-svg-options" id="practice-options-grid">
                ${practiceQ.options.map((opt, idx) => `
                  <button class="iq-option-btn practice-opt-btn" data-idx="${idx}" aria-label="${opt.id}: ${isAr ? opt.text_ar : opt.text_en}">
                    <span class="iq-opt-letter">${opt.id}</span>
                    <span class="iq-opt-content" dir="auto">${opt.svg ? opt.svg : (isAr ? opt.text_ar : opt.text_en)}</span>
                  </button>
                `).join('')}
              </div>
            </section>
            <div id="practice-feedback" class="iq-practice-feedback" style="display:none;" aria-live="polite"></div>
          </div>
        `;
        introCard.appendChild(sampleDetails);
      }
    }

    // Handle practice buttons inside sample accordion
    const practiceBtns = container.querySelectorAll('.practice-opt-btn');
    const feedbackBox = document.getElementById('practice-feedback');
    practiceBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = parseInt(btn.dataset.idx, 10);
        practiceBtns.forEach(b => b.classList.remove('selected', 'correct', 'wrong'));
        btn.classList.add('selected');

        if (idx === practiceQ.correctIndex) {
          btn.classList.add('correct');
          feedbackBox.className = 'iq-practice-feedback success';
          feedbackBox.textContent = isAr ? `✔ إجابة صحيحة! ${practiceQ.explanation_ar}` : `✔ Correct! ${practiceQ.explanation_en}`;
        } else {
          btn.classList.add('wrong');
          feedbackBox.className = 'iq-practice-feedback notice';
          feedbackBox.textContent = isAr ? `حاول مجددًا. ${practiceQ.explanation_ar}` : `Try again. ${practiceQ.explanation_en}`;
        }
        feedbackBox.style.display = 'block';
      });
    });

    // Start button
    const startBtn = document.getElementById('iq-start-btn');
    startBtn.addEventListener('click', startNewTest);
  }

  function startNewTest() {
    trackEvent('tool_started', 'iq-test');
    prng = createPRNG(createSessionSeed());
    session = new AdaptiveTestSession(prng, 28);
    remainingSeconds = 20 * 60; // 1200 seconds
    lastTimeMs = getNow();

    renderActiveTestLayout();
    startSessionTimer();
    serveNextQuestion();

    // Auto scroll to top cleanly
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function renderActiveTestLayout() {
    container.innerHTML = `
      <div class="iq-flow-card iq-test-card" role="region" aria-label="${isAr ? 'جلسة الاختبار' : 'Active Test Session'}">
        <header class="iq-test-topbar">
          <div class="iq-topbar-domain">
            <span id="iq-domain-pill" class="iq-domain-pill">--</span>
          </div>

          <div class="iq-topbar-meta">
            <span id="iq-q-counter" class="iq-q-counter" aria-live="polite">السؤال 01 من 28</span>
            <div id="iq-timer-box" class="iq-timer-box" aria-label="${isAr ? 'الوقت المتبقي' : 'Time Remaining'}">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <span id="iq-timer-val">20:00</span>
            </div>
          </div>
        </header>

        <div class="iq-progress-track" aria-hidden="true">
          <div id="iq-progress-bar" class="iq-progress-bar" style="width: 3.5%;"></div>
        </div>

        <main class="iq-question-stage">
          <h3 id="iq-question-text" class="iq-question-text" dir="auto">...</h3>
          <div id="iq-stimulus-container" class="iq-stimulus-container"></div>
          <section class="iq-options-container" role="region" aria-label="${isAr ? 'منطقة خيارات الإجابة' : 'Answer choices section'}">
            <div class="iq-options-header">
              <span class="iq-options-title">${isAr ? 'حدد الإجابة المناسبة:' : 'Select Answer:'}</span>
            </div>
            <div id="iq-options-grid" class="iq-options-grid" role="group" aria-label="${isAr ? 'خيارات الإجابة' : 'Answer Choices'}"></div>

            <div class="iq-confirm-wrapper">
              <button id="iq-confirm-btn" class="iq-confirm-btn" type="button" disabled aria-disabled="true">
                <span class="iq-confirm-icon" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </span>
                <span class="iq-confirm-text">${isAr ? 'تأكيد ومتابعة' : 'Confirm & Next'}</span>
              </button>
            </div>
          </section>
        </main>

        <footer class="iq-test-footer">
          <div id="iq-calm-status" class="iq-calm-status" aria-live="polite"></div>
          <button id="iq-quit-btn" class="iq-quit-btn" title="${isAr ? 'إنهاء الاختبار وعرض النتيجة الحالية' : 'End test & view current score'}">
            ${isAr ? 'إنهاء وحساب النتيجة' : 'End & Compute Score'}
          </button>
        </footer>
      </div>
    `;

    const quitBtn = document.getElementById('iq-quit-btn');
    quitBtn.addEventListener('click', async () => {
      const confirmed = await showConfirm(
        isAr ? 'هل أنت متأكد من رغبتك في إنهاء الاختبار وحساب نتيجتك الحالية الآن؟' : 'Are you sure you want to end the test and calculate your current score?',
        {
          title: isAr ? 'إنهاء وحساب النتيجة' : 'End Assessment',
          confirmText: isAr ? 'إنهاء الاختبار' : 'End Test',
          cancelText: isAr ? 'متابعة الاختبار' : 'Keep Going',
          confirmVariant: 'primary'
        }
      );
      if (confirmed) {
        finishTest();
      }
    });

    const confirmBtn = document.getElementById('iq-confirm-btn');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => {
        if (pendingSelectedIndex === null || confirmBtn.disabled) return;
        confirmCurrentAnswer();
      });
    }
  }

  function startSessionTimer() {
    clearInterval(timerInterval);
    lastTimeMs = getNow();

    timerInterval = setInterval(() => {
      const now = getNow();
      const elapsed = Math.floor((now - lastTimeMs) / 1000);
      if (elapsed >= 1) {
        remainingSeconds = Math.max(0, remainingSeconds - elapsed);
        lastTimeMs = now;
        updateTimerDisplay();

        if (remainingSeconds <= 0) {
          clearInterval(timerInterval);
          finishTest();
        }
      }
    }, 500);
  }

  function updateTimerDisplay() {
    const timerVal = document.getElementById('iq-timer-val');
    if (!timerVal) return;
    const m = Math.floor(remainingSeconds / 60);
    const s = remainingSeconds % 60;
    timerVal.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  function serveNextQuestion() {
    currentQuestion = session.getNextQuestion();
    if (!currentQuestion) {
      finishTest();
      return;
    }

    questionStartTime = getNow();

    // Update Progress & Badges
    const domainObj = DOMAINS.find(d => d.id === currentQuestion.domainId) || DOMAINS[0];
    const domainPill = document.getElementById('iq-domain-pill');
    if (domainPill) {
      domainPill.textContent = isAr ? domainObj.name_ar : domainObj.name_en;
      domainPill.style.borderColor = domainObj.color;
    }

    const qCounter = document.getElementById('iq-q-counter');
    if (qCounter) {
      const numStr = String(currentQuestion.sessionNumber).padStart(2, '0');
      qCounter.textContent = isAr ? `السؤال ${numStr} من 28` : `Question ${numStr} of 28`;
    }

    const progressBar = document.getElementById('iq-progress-bar');
    if (progressBar) {
      const pct = Math.round((currentQuestion.sessionNumber / 28) * 100);
      progressBar.style.width = `${pct}%`;
    }

    // Render Question Text & Stimulus
    const qText = document.getElementById('iq-question-text');
    if (qText) {
      qText.textContent = isAr ? currentQuestion.questionText_ar : currentQuestion.questionText_en;
    }

    const stimContainer = document.getElementById('iq-stimulus-container');
    if (stimContainer) {
      const stim = isAr
        ? (currentQuestion.stimulusHtml_ar || currentQuestion.stimulusHtml || '')
        : (currentQuestion.stimulusHtml_en || currentQuestion.stimulusHtml || '');
      stimContainer.innerHTML = stim;
      stimContainer.style.display = stim ? 'flex' : 'none';
    }

    // Reset pending selection
    pendingSelectedIndex = null;
    const confirmBtn = document.getElementById('iq-confirm-btn');
    if (confirmBtn) {
      confirmBtn.disabled = true;
      confirmBtn.setAttribute('aria-disabled', 'true');
      confirmBtn.classList.remove('is-ready');
    }

    // Render Options
    const optionsGrid = document.getElementById('iq-options-grid');
    if (optionsGrid) {
      const hasSvg = currentQuestion.options.some(o => !!o.svg);
      const isLongText = currentQuestion.options.some(o => (o.text_ar && o.text_ar.length > 40) || (o.text_en && o.text_en.length > 40));

      optionsGrid.className = 'iq-options-grid';
      if (hasSvg) optionsGrid.classList.add('has-svg-options');
      if (isLongText) optionsGrid.classList.add('has-long-text');

      optionsGrid.innerHTML = currentQuestion.options.map((opt, idx) => `
        <button type="button" class="iq-option-btn active-opt-btn" data-idx="${idx}" aria-pressed="false" aria-label="${opt.id}: ${isAr ? (opt.text_ar || opt.id) : (opt.text_en || opt.id)}">
          <span class="iq-opt-letter">${opt.id}</span>
          <span class="iq-opt-content" dir="auto">${opt.svg ? opt.svg : (isAr ? (opt.text_ar || '') : (opt.text_en || ''))}</span>
        </button>
      `).join('');

      const optBtns = optionsGrid.querySelectorAll('.active-opt-btn');
      optBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          handleOptionClick(parseInt(btn.dataset.idx, 10), btn);
        });
      });
    }

    // Clear status
    const statusBox = document.getElementById('iq-calm-status');
    if (statusBox) statusBox.textContent = '';
  }

  function handleOptionClick(selectedIndex, clickedBtn) {
    pendingSelectedIndex = selectedIndex;

    const optBtns = container.querySelectorAll('.active-opt-btn');
    optBtns.forEach(b => {
      b.classList.remove('selected');
      b.setAttribute('aria-pressed', 'false');
    });
    clickedBtn.classList.add('selected');
    clickedBtn.setAttribute('aria-pressed', 'true');

    const confirmBtn = document.getElementById('iq-confirm-btn');
    if (confirmBtn) {
      confirmBtn.disabled = false;
      confirmBtn.setAttribute('aria-disabled', 'false');
      confirmBtn.classList.add('is-ready');
    }
  }

  function confirmCurrentAnswer() {
    if (pendingSelectedIndex === null) return;
    const elapsedMs = Math.round(getNow() - questionStartTime);

    // Disable all options and confirm button immediately during transition
    const confirmBtn = document.getElementById('iq-confirm-btn');
    if (confirmBtn) {
      confirmBtn.disabled = true;
      confirmBtn.setAttribute('aria-disabled', 'true');
      confirmBtn.classList.remove('is-ready');
    }

    const optBtns = container.querySelectorAll('.active-opt-btn');
    optBtns.forEach(b => {
      b.disabled = true;
    });

    // Show calm immediate confirmation message
    const statusBox = document.getElementById('iq-calm-status');
    if (statusBox) {
      statusBox.textContent = isAr ? '✔ تم تأكيد إجابتك' : '✔ Answer confirmed';
    }

    // Record response in adaptive session (does NOT reveal correctness to user)
    session.recordResponse(currentQuestion, pendingSelectedIndex, elapsedMs);

    // Smooth quick transition (120ms)
    setTimeout(() => {
      serveNextQuestion();
    }, 120);
  }

  function finishTest() {
    clearInterval(timerInterval);

    latestScores = computeSessionScores(session.getRecords(), 28, lang);
    trackEvent('tool_completed', 'iq-test', {
      completed_questions: session.getRecords().length,
      iq: latestScores.performanceIndex
    });
    renderResultsView(latestScores);
    container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function renderResultsView(scores) {
    const userTier = getUserIqTier(scores.performanceIndex);
    const estimatedIq = calculateEstimatedIq(scores.performanceIndex);

    container.innerHTML = `
      <div class="iq-flow-card iq-results-card" role="region" aria-label="${isAr ? 'تقرير نتائج الاختبار' : 'Test Results Report'}">
        <header class="iq-results-header">
          <div class="iq-intro-badge success">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            <span>${isAr ? 'اكتمل الاختبار بنجاح' : 'Assessment Successfully Completed'}</span>
          </div>
          <h2 class="iq-results-title">${isAr ? 'الملف المعرفي ومعدل الذكاء التقديري' : 'Cognitive Profile & Estimated IQ'}</h2>
          <div class="iq-exploratory-disclaimer" role="note">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            <span>${isAr ? 'تنويه علمي: هذه النتائج استكشافية لأغراض التدريب والتطوير الذاتي ومقاسة وفق معايير ويشسلر وستانفورد بينيه للتوزيع الطبيعي.' : 'Scientific Notice: These results provide exploratory cognitive insights calibrated against standardized Wechsler & Stanford-Binet normal distribution.'}</span>
          </div>
        </header>

        <section class="iq-score-hero">
          <div class="iq-main-meter-wrap">
            <div class="iq-score-dial iq-estimated-dial">
              <span class="iq-dial-number">${estimatedIq}</span>
              <span class="iq-dial-label">${isAr ? 'درجة IQ المقدرة' : 'Estimated IQ'}</span>
            </div>
            <span class="iq-dial-subtext">${isAr ? 'مقياس ويشسلر المعياري (المتوسط = 100)' : 'Standard Psychometric Scale (Mean = 100)'}</span>
            <span class="iq-user-tier-tag" style="border-color: ${userTier.color}; color: ${userTier.color}; background: ${userTier.color}22;">
              ${isAr ? userTier.name_ar : userTier.name_en}
            </span>
          </div>

          <div class="iq-stats-summary-grid">
            <div class="iq-stat-card iq-stat-highlight">
              <span class="iq-stat-card-val">${scores.performanceIndex} / 100</span>
              <span class="iq-stat-card-lbl">${isAr ? 'مؤشر الأداء المعرفي' : 'Cognitive Performance Index'}</span>
            </div>
            <div class="iq-stat-card">
              <span class="iq-stat-card-val">${scores.correctCount} / ${scores.answeredCount}</span>
              <span class="iq-stat-card-lbl">${isAr ? `الدقة التحليلية (${scores.accuracyPct}%)` : `Analytical Accuracy (${scores.accuracyPct}%)`}</span>
            </div>
            <div class="iq-stat-card">
              <span class="iq-stat-card-val" style="color: #10b981;">+${scores.fluencyBonusPct}%</span>
              <span class="iq-stat-card-lbl">${isAr ? 'بونص الطلاقة والسرعة' : 'Mental Fluency Bonus'}</span>
            </div>
            <div class="iq-stat-card">
              <span class="iq-stat-card-val">${(scores.medianTimeMs / 1000).toFixed(1)}s</span>
              <span class="iq-stat-card-lbl">${isAr ? `وسيط الاستجابة (${scores.speedCategory})` : `Median Time (${scores.speedCategory})`}</span>
            </div>
          </div>
        </section>

        <!-- Fast Actions Toolbar: Immediately below the initial score, above 7 domains -->
        <div class="iq-results-actions" role="toolbar" aria-label="${isAr ? 'إجراءات النتيجة' : 'Assessment Actions'}">
          <button id="iq-retry-btn" class="btn-primary iq-act-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
            <span>${isAr ? 'بدء اختبار جديد بأسئلة مختلفة' : 'Start Fresh Test with New Questions'}</span>
          </button>

          <button id="iq-copy-btn" class="btn-secondary iq-act-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
            <span id="iq-copy-btn-text">${isAr ? 'نسخ ملخص النتيجة' : 'Copy Summary'}</span>
          </button>

          <button id="iq-review-btn" class="btn-secondary iq-act-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            <span>${isAr ? 'مراجعة الحلول والتحليل المنطقي' : 'Review Solutions & Logic'}</span>
          </button>
        </div>

        <section class="iq-domains-breakdown-section">
          <h3 class="iq-section-subheading">${isAr ? 'توزيع القدرات عبر المجالات المعرفية السبعة' : 'Cognitive Breakdown Across 7 Domains'}</h3>
          
          <div class="iq-radar-and-bars-grid">
            <div class="iq-radar-col">
              ${scores.radarSvg}
            </div>

            <div class="iq-bars-col">
              ${DOMAINS.map(d => {
                const stat = scores.domainStats[d.id] || { correct: 0, total: 4, scorePct: 0, bonusPct: 0 };
                const pct = stat.scorePct ?? (stat.total > 0 ? Math.round((stat.correct / stat.total) * 100) : 0);
                const bonusTag = stat.bonusPct > 0 ? ` (+${stat.bonusPct}%)` : '';
                return `
                  <div class="iq-domain-bar-row">
                    <div class="iq-bar-header">
                      <span class="iq-bar-title">${isAr ? d.name_ar : d.name_en}</span>
                      <span class="iq-bar-score">${stat.correct} / ${stat.total}${bonusTag} — ${pct}%</span>
                    </div>
                    <div class="iq-bar-track">
                      <div class="iq-bar-fill" style="width: ${pct}%; background: ${d.color};"></div>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        </section>

        <section class="iq-insights-section">
          <div class="iq-insights-card strength">
            <h4 class="iq-insight-title">${isAr ? '🌟 أبرز مجالات القوة' : '🌟 Prominent Strengths'}</h4>
            <p>${scores.strengths.length > 0 ? scores.strengths.map(s => s.name).join(' و ') : (isAr ? 'أداء متوازن عبر مختلف المجالات المعرفية' : 'Balanced performance across all domains')}.</p>
          </div>
          <div class="iq-insights-card growth">
            <h4 class="iq-insight-title">${isAr ? '🎯 مجالات مقترحة للتدريب الإضافي' : '🎯 Suggested Practice Areas'}</h4>
            <p>${scores.growthAreas.length > 0 ? scores.growthAreas.map(g => g.name).join(' و ') : (isAr ? 'المحافظة على الممارسة الذهنية المستمرة' : 'Maintain ongoing regular cognitive practice')}.</p>
          </div>
        </section>

        <!-- Global Human IQ Levels & Smartest Humans Comparison -->
        <section class="iq-benchmark-section" aria-labelledby="iq-benchmark-heading">
          <header class="iq-benchmark-header">
            <h3 id="iq-benchmark-heading" class="iq-section-subheading">
              ${isAr ? '🌐 مستويات ومتوسط ذكاء البشر عالمياً وموقعك فيها' : '🌐 Global Human IQ Distribution & Your Ranking'}
            </h3>
            <p class="iq-benchmark-subtitle">
              ${isAr ? 'التوزيع الطبيعي المعياري المعتمد دولياً (مقياس ويشسلر وستانفورد بينيه — متوسط عامة البشر = 100 بانحراف معياري 15):' : 'Standardized Wechsler & Stanford-Binet psychometric bell curve (Human Mean = 100, SD = 15):'}
            </p>
          </header>

          <div class="iq-distribution-board">
            <div class="iq-personal-score-banner">
              <div class="iq-ps-left">
                <span class="iq-ps-tag">${isAr ? 'درجتك المقدرة' : 'Your Score'}</span>
                <strong class="iq-ps-val">${estimatedIq} <span class="iq-ps-unit">IQ</span></strong>
              </div>
              <div class="iq-ps-right">
                <span class="iq-ps-tier-title">${isAr ? userTier.name_ar : userTier.name_en} (${userTier.range})</span>
                <p class="iq-ps-tier-desc">${isAr ? `أداؤك يضعك ضمن نطاق (${userTier.range}) الذي يمثل نحو ${userTier.percent}% من عامة البشر حول العالم وفق منحنى التوزيع الطبيعي.` : `Your performance places you within the (${userTier.range}) range representing approx. ${userTier.percent}% of the global population on the standard normal distribution.`}</p>
              </div>
            </div>

            <div class="iq-distribution-list">
              ${HUMAN_IQ_TIERS.map(tier => {
                const isUserTier = userTier.id === tier.id;
                return `
                  <div class="iq-dist-tier ${isUserTier ? 'user-tier-highlight' : ''} ${tier.isAverage ? 'avg-tier' : ''}">
                    <div class="iq-dist-tier-range">
                      <strong class="iq-dist-range-val">${tier.range}</strong>
                      ${tier.isAverage ? `<span class="iq-avg-pill">${isAr ? 'متوسط عامة البشر (100)' : 'General Human Average (100)'}</span>` : ''}
                      ${isUserTier ? `<span class="iq-user-pill">👈 ${isAr ? `درجتك الشخصية: ${estimatedIq}` : `Your Score: ${estimatedIq}`}</span>` : ''}
                    </div>
                    <div class="iq-dist-tier-info">
                      <span class="iq-dist-tier-name">${isAr ? tier.name_ar : tier.name_en}</span>
                      <span class="iq-dist-tier-pct">${tier.percent}% ${isAr ? 'من البشر عالمياً' : 'of world population'}</span>
                    </div>
                    <div class="iq-dist-tier-bar">
                      <div class="iq-dist-tier-fill" style="width: ${tier.barPct}%; background: ${tier.color};"></div>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>

          <div class="iq-geniuses-block">
            <header class="iq-geniuses-header">
              <h4 class="iq-geniuses-title">
                ${isAr ? '🧠 أرقام ومقارنة أبرز أذكى عقول البشر في التاريخ' : '🧠 Prominent High-IQ Minds Recorded in History'}
              </h4>
              <p class="iq-geniuses-note">
                ${isAr ? 'أرقام حقيقية وتقديرات موثقة مستندة إلى دراسات كاثرينا كوكس النفسية بجامعة ستانفورد وسجلات القياس الفكري العالمية وموسوعة جينيس:' : 'Real psychometric benchmarks from Stanford biographical research (Dr. Catharine Cox) and verified historical records:'}
              </p>
            </header>

            <div class="iq-geniuses-grid">
              ${SMARTEST_HUMANS.map(person => `
                <article class="iq-genius-card">
                  <div class="iq-genius-top">
                    <span class="iq-genius-score">IQ ${person.iq}</span>
                    <span class="iq-genius-field">${isAr ? person.field_ar : person.field_en}</span>
                  </div>
                  <h5 class="iq-genius-name">${isAr ? person.name_ar : person.name_en}</h5>
                  <p class="iq-genius-desc">${isAr ? person.achievement_ar : person.achievement_en}</p>
                </article>
              `).join('')}
            </div>
          </div>
        </section>
      </div>
    `;

    // Event handlers
    const reviewBtn = document.getElementById('iq-review-btn');
    if (reviewBtn) {
      reviewBtn.addEventListener('click', () => renderReviewView());
    }

    const retryBtn = document.getElementById('iq-retry-btn');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        renderIntroView();
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    const copyBtn = document.getElementById('iq-copy-btn');
    const copyBtnText = document.getElementById('iq-copy-btn-text');
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(scores.summaryText).then(() => {
            copyBtnText.textContent = isAr ? '✔ تم نسخ الملخص' : '✔ Summary Copied';
            setTimeout(() => {
              copyBtnText.textContent = isAr ? 'نسخ ملخص النتيجة' : 'Copy Summary';
            }, 2500);
          });
        }
      });
    }
  }

  function renderReviewView() {
    container.scrollIntoView({ behavior: 'smooth', block: 'start' });

    let records = session?.getAllReviewItems ? session.getAllReviewItems(currentQuestion) : (session?.getRecords?.() || []);
    if ((!records || records.length === 0) && session?.getAllReviewItems) {
      records = session.getAllReviewItems(null);
    }

    const answeredCount = (records || []).filter(r => !r.isUnanswered && r.selectedIndex !== null && r.selectedIndex !== undefined).length;
    const totalCount = records ? records.length : 28;
    const isEarlyEnd = answeredCount < totalCount;
    const isAllUnanswered = answeredCount === 0;

    container.innerHTML = `
      <div class="iq-flow-card iq-review-card" role="region" aria-label="${isAr ? 'مراجعة حلول الاختبار' : 'Solution Review'}">
        <header class="iq-review-header">
          <button id="iq-back-results-btn" class="btn-secondary" style="min-height: 40px; padding: 6px 14px;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="${isAr ? 'M5 12h14M12 5l7 7-7 7' : 'M19 12H5M12 19l-7-7 7-7'}"/>
            </svg>
            <span>${isAr ? 'العودة لتقرير النتيجة' : 'Back to Results Report'}</span>
          </button>
          <h2 class="iq-results-title">${isAr ? 'مراجعة وتفسير جميع الأسئلة' : 'Comprehensive Question Review & Explanations'}</h2>
          ${isAllUnanswered ? `
            <div class="iq-review-summary-banner" role="note">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <span>${isAr ? `يعرض هذا التقرير جميع أسئلة الاختبار الـ (${totalCount}) مع الإجابات النموذجية الصحيحة والتحليل المنطقي المفصل لكل سؤال لتتمكن من دراستها والتدريب عليها.` : `This review displays all (${totalCount}) assessment questions with model solutions and comprehensive logical explanations for your learning and practice.`}</span>
            </div>
          ` : isEarlyEnd ? `
            <div class="iq-review-summary-banner" role="note">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <span>${isAr ? `تتضمن المراجعة الأسئلة الـ (${answeredCount}) التي أجبت عليها، بالإضافة إلى باقي أسئلة الاختبار الـ (${totalCount}) مع توضيح الحل الصحيح والتحليل المنطقي الكامل.` : `Review includes the (${answeredCount}) answered questions plus the remaining test questions (${totalCount} total) with correct solutions and full logical explanations.`}</span>
            </div>
          ` : `
            <div class="iq-review-summary-banner" role="note">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
              <span>${isAr ? `مراجعة شاملة لجميع إجاباتك الـ (${totalCount}) مع الحلول النموذجية والتحليل المنطقي المفصل لكل مهمة.` : `Complete review of all (${totalCount}) items with verified solutions and logical rationales.`}</span>
            </div>
          `}
        </header>

        <div class="iq-review-list">
          ${records.map((r, idx) => {
            const q = r.question;
            const d = DOMAINS.find(dm => dm.id === q.domainId) || DOMAINS[0];
            const isUnanswered = r.isUnanswered || r.selectedIndex === null || r.selectedIndex === undefined;
            const isCorrect = !isUnanswered && r.isCorrect;

            let badgeHtml = '';
            let itemClass = 'iq-review-item';
            if (isUnanswered) {
              itemClass += ' item-unanswered';
              badgeHtml = `<span class="iq-review-badge unanswered">${isAr ? '⚪ لم تتم الإجابة' : '⚪ Unanswered'}</span>`;
            } else if (isCorrect) {
              itemClass += ' item-correct';
              badgeHtml = `<span class="iq-review-badge correct">${isAr ? '✔ إجابة صحيحة' : '✔ Correct'}</span>`;
            } else {
              itemClass += ' item-wrong';
              badgeHtml = `<span class="iq-review-badge wrong">${isAr ? '✖ إجابة خاطئة' : '✖ Incorrect'}</span>`;
            }

            return `
              <article class="${itemClass}">
                <div class="iq-review-top">
                  <div class="iq-review-meta">
                    <span class="iq-review-num">${isAr ? `السؤال ${idx + 1} من ${records.length}` : `Question ${idx + 1} of ${records.length}`}</span>
                    <span class="iq-domain-tag" style="background:${d.color}20; color:${d.color}; border:1px solid ${d.color}50;">${isAr ? d.name_ar : d.name_en}</span>
                  </div>
                  ${badgeHtml}
                </div>

                <p class="iq-review-q-text" dir="auto">${isAr ? q.questionText_ar : q.questionText_en}</p>
                ${(q.stimulusHtml_ar || q.stimulusHtml_en || q.stimulusHtml) ? `<div class="iq-review-stimulus">${isAr ? (q.stimulusHtml_ar || q.stimulusHtml) : (q.stimulusHtml_en || q.stimulusHtml)}</div>` : ''}

                ${(() => {
                  const hasLongText = q.options.some(o => (o.text_ar && o.text_ar.length > 40) || (o.text_en && o.text_en.length > 40));
                  const hasSvg = q.options.some(o => !!o.svg);
                  let gridClass = 'iq-review-choices-grid';
                  if (hasLongText) gridClass += ' has-long-text';
                  if (hasSvg) gridClass += ' has-svg-options';
                  return `<div class="${gridClass}">`;
                })()}
                  ${q.options.map((opt, oIdx) => {
                    const isSelected = !isUnanswered && r.selectedIndex === oIdx;
                    const isRightAnswer = q.correctIndex === oIdx;
                    let optClass = 'iq-review-choice';
                    if (isRightAnswer) optClass += ' choice-right';
                    else if (isSelected && !isRightAnswer) optClass += ' choice-wrong';

                    return `
                      <div class="${optClass}">
                        <span class="iq-opt-letter">${opt.id}</span>
                        <span class="iq-opt-content" dir="auto">${opt.svg ? opt.svg : (isAr ? (opt.text_ar || opt.id) : (opt.text_en || opt.id))}</span>
                        ${isSelected ? `<span class="iq-user-picked-label">${isAr ? '(اختيارك)' : '(Your Choice)'}</span>` : ''}
                        ${isUnanswered && isRightAnswer ? `<span class="iq-user-picked-label" style="color:var(--status-success,#10b981); font-weight:750;">${isAr ? '(الحل الصحيح)' : '(Correct Solution)'}</span>` : ''}
                      </div>
                    `;
                  }).join('')}
                </div>

                <div class="iq-review-explanation" dir="auto">
                  <strong>${isAr ? '💡 التحليل المنطقي للحل:' : '💡 Logical Solution Explanation:'}</strong>
                  <p dir="auto">${isAr ? q.explanation_ar : q.explanation_en}</p>
                </div>
              </article>
            `;
          }).join('')}
        </div>

        <div style="text-align:center; margin-block-start:var(--space-6);">
          <button id="iq-bottom-back-btn" class="btn-primary" style="min-width: 200px; min-height: 48px;">
            <span>${isAr ? 'العودة لتقرير النتيجة' : 'Back to Results Report'}</span>
          </button>
        </div>
      </div>
    `;

    const backBtn = document.getElementById('iq-back-results-btn');
    const bottomBackBtn = document.getElementById('iq-bottom-back-btn');
    const goBack = () => {
      renderResultsView(latestScores);
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (backBtn) backBtn.addEventListener('click', goBack);
    if (bottomBackBtn) bottomBackBtn.addEventListener('click', goBack);
  }
}
